import { useState, useEffect } from 'react';
import { commentApi, userApi, Comment, User } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useAppStore } from '@/stores/appStore';
import { CommentInput } from './CommentInput';
import { CommentItem } from './CommentItem';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface CommentsTabProps {
  initiativeId: string;
  onCommentCountChange?: (count: number) => void;
}

export function CommentsTab({ initiativeId, onCommentCountChange }: CommentsTabProps) {
  const { user } = useAuth();
  const updateInitiativeCommentCount = useAppStore((state) => state.updateInitiativeCommentCount);
  const triggerRefresh = useAppStore((state) => state.triggerRefresh);
  const [comments, setComments] = useState<Comment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string;
    hasReplies: boolean;
  } | null>(null);

  const canComment = user?.role === 'ADMIN' || user?.role === 'EDITOR';

  const loadComments = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await commentApi.getByInitiative(initiativeId);
      setComments(data);

      // Calculate total count including replies
      const totalCount = data.reduce(
        (acc, comment) => acc + 1 + (comment.replies?.length || 0),
        0
      );
      // Update modal tab badge
      onCommentCountChange?.(totalCount);
      // Update store so Timeline/List views reflect new count immediately
      updateInitiativeCommentCount(initiativeId, totalCount);
    } catch (err) {
      console.error('Failed to load comments:', err);
      setError('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComments();
    // Load users for mention autocomplete (best-effort, ignore errors)
    userApi.getAll().then(setUsers).catch(() => {});
  }, [initiativeId]);

  const handleAddComment = async (content: string) => {
    try {
      await commentApi.create(initiativeId, content);
      await loadComments();
      triggerRefresh();
    } catch (err) {
      console.error('Failed to add comment:', err);
      throw err;
    }
  };

  const handleReply = async (parentId: string, content: string) => {
    try {
      await commentApi.create(initiativeId, content, parentId);
      await loadComments();
      triggerRefresh();
    } catch (err) {
      console.error('Failed to add reply:', err);
      throw err;
    }
  };

  const handleEdit = async (commentId: string, content: string) => {
    try {
      await commentApi.update(commentId, content);
      await loadComments();
      triggerRefresh();
    } catch (err) {
      console.error('Failed to edit comment:', err);
      throw err;
    }
  };

  const handleDeleteClick = (commentId: string, hasReplies: boolean) => {
    setDeleteConfirm({ id: commentId, hasReplies });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    try {
      await commentApi.delete(deleteConfirm.id);
      setDeleteConfirm(null);
      await loadComments();
      triggerRefresh();
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const handlePin = async (commentId: string) => {
    try {
      await commentApi.pin(commentId);
      await loadComments();
    } catch (err) {
      console.error('Failed to pin comment:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-sm text-gray-500">Loading comments...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-sm text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Add comment input at top - only for editors/admins */}
      {canComment && (
        <div className="pb-4 border-b border-gray-100">
          <CommentInput
            onSubmit={handleAddComment}
            placeholder="Write a comment..."
            users={users}
          />
        </div>
      )}

      {/* Comments list */}
      <div className="flex-1 overflow-y-auto py-4 space-y-4">
        {comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-gray-500">
            <p className="text-sm">No comments yet</p>
            {canComment && (
              <p className="text-xs mt-1">Be the first to comment</p>
            )}
          </div>
        ) : (
          (() => {
            const sortedComments = [...comments].sort((a, b) => {
              if (a.isPinned && !b.isPinned) return -1;
              if (!a.isPinned && b.isPinned) return 1;
              return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            });
            return sortedComments.map((comment) =>
              comment.isPinned ? (
                <div key={comment.id}>
                  {/* Highlight box covers only the pinned comment itself — replies render below, unhighlighted.
                      No "Pinned comment" label: the solid pin icon in the comment's own Pin/Unpin action already says so. */}
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
                    <CommentItem
                      comment={comment}
                      currentUserId={user?.id}
                      canComment={canComment}
                      onReply={handleReply}
                      onEdit={handleEdit}
                      onDelete={handleDeleteClick}
                      onPin={handlePin}
                      canPin={canComment}
                      hideReplies
                    />
                  </div>
                  {comment.replies && comment.replies.length > 0 && (
                    <div className="mt-3 border-l-2 border-gray-100 pl-3">
                      {comment.replies.map((reply) => (
                        <CommentItem
                          key={reply.id}
                          comment={reply}
                          currentUserId={user?.id}
                          canComment={canComment}
                          onReply={handleReply}
                          onEdit={handleEdit}
                          onDelete={handleDeleteClick}
                          onPin={handlePin}
                          canPin={canComment}
                          isReply
                        />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  currentUserId={user?.id}
                  canComment={canComment}
                  onReply={handleReply}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  onPin={handlePin}
                  canPin={canComment}
                />
              )
            );
          })()
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Delete Comment
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  {deleteConfirm.hasReplies
                    ? 'This comment has replies. Deleting it will also delete all replies. This action cannot be undone.'
                    : 'Are you sure you want to delete this comment? This action cannot be undone.'}
                </p>
                <div className="mt-4 flex justify-end gap-3">
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
